/**
 * API Services pour la sécurité et l'audit
 * RÉUTILISABILITÉ : 100% - TypeScript pur
 */

import { apiClient } from '@shared/infrastructure/http';
import { getAuthConfig } from '../../config/authConfig';
import type {
  ApiResponse,
  SecurityParams,
  SecuritySettings,
  UpdateSecuritySettingsData,
  LoginAttemptsResult,
  HistoryResult,
  GetSecurityLogsData,
  SecurityLogsResult,
  RequestPasswordResetData,
  PasswordResetResult,
  VerifyResetTokenData,
  ResetPasswordData,
  ValidateTokenData,
  TokenValidation,
  RevokeTokenData,
  RevokeAllTokensData,
} from '../../application/dto/dtos';

/**
 * Service sécurité et audit
 */
export const securityApi = {
  /**
   * Récupérer les paramètres de sécurité d'un utilisateur
   */
  async getSecurityParams(idUtilisateur: string): Promise<ApiResponse<SecurityParams>> {
    const config = getAuthConfig();
    // Remplacer :id par idUtilisateur dans l'URL
    const url = config.endpoints.getSecurityParams.replace(':id', idUtilisateur);
    return apiClient.get<SecurityParams>(url);
  },

  /**
   * Récupérer les paramètres de sécurité détaillés
   */
  async getSecuritySettings(data: { idUtilisateur: string }): Promise<ApiResponse<SecuritySettings>> {
    const config = getAuthConfig();
    const url = `${config.endpoints.getSecurityParams}/${data.idUtilisateur}`;
    return apiClient.get<SecuritySettings>(url);
  },

  /**
   * Mettre à jour les paramètres de sécurité
   */
  async updateSecuritySettings(data: UpdateSecuritySettingsData): Promise<ApiResponse<void>> {
    const config = getAuthConfig();
    return apiClient.put<void>(config.endpoints.updateSecurityParams || '/api/auth/parametres', data);
  },

  /**
   * Bloquer une adresse IP (blacklist)
   */
  async blockIP(data: {
    adresseIP: string;
    raison: string;
    typeBlocage: 'TEMPORAIRE' | 'PERMANENT';
    dureeHeures?: number;
  }): Promise<ApiResponse<void>> {
    return apiClient.post<void>('/api/auth/security/block-ip', data);
  },

  /**
   * Debloquer une adresse IP
   */
  async unblockIP(adresseIP: string): Promise<ApiResponse<void>> {
    return apiClient.post<void>('/api/auth/security/unblock-ip', { adresseIP });
  },

  /**
   * Récupérer les tentatives de connexion
   */
  async getLoginAttempts(data: { idUtilisateur: string; limite?: number }): Promise<ApiResponse<LoginAttemptsResult>> {
    const config = getAuthConfig();
    const params = new URLSearchParams();
    params.append('idUtilisateur', data.idUtilisateur);
    if (data.limite) params.append('limite', String(data.limite));
    const url = `${config.endpoints.getHistory}?${params.toString()}`;
    return apiClient.get<LoginAttemptsResult>(url);
  },

  /**
   * Récupérer l'historique des actions de l'utilisateur
   */
  async getHistory(
    idUtilisateur: string,
    options?: {
      type?: 'TOUS' | 'CONNEXIONS' | 'MODIFICATIONS' | 'SECURITE';
      page?: number;
      limit?: number;
    }
  ): Promise<ApiResponse<HistoryResult>> {
    const config = getAuthConfig();

    const params = new URLSearchParams();
    params.append('idUtilisateur', idUtilisateur);
    if (options?.type) params.append('type', options.type);
    if (options?.page) params.append('page', String(options.page));
    if (options?.limit) params.append('limit', String(options.limit));

    const url = `${config.endpoints.getHistory}?${params.toString()}`;
    return apiClient.get<HistoryResult>(url);
  },

  /**
   * Récupérer les logs de sécurité
   */
  async getSecurityLogs(data: GetSecurityLogsData): Promise<ApiResponse<SecurityLogsResult>> {
    const config = getAuthConfig();

    const params = new URLSearchParams();
    params.append('idUtilisateur', data.idUtilisateur);
    if (data.type) params.append('type', data.type);
    if (data.niveau) params.append('niveau', data.niveau);
    if (data.dateDebut) params.append('dateDebut', data.dateDebut.toISOString());
    if (data.dateFin) params.append('dateFin', data.dateFin.toISOString());
    if (data.limite) params.append('limite', String(data.limite));

    const url = `${config.endpoints.getSecurityLogs}?${params.toString()}`;
    return apiClient.get<SecurityLogsResult>(url);
  },

  /**
   * Demander une réinitialisation de mot de passe
   */
  async requestPasswordReset(data: RequestPasswordResetData): Promise<ApiResponse<PasswordResetResult>> {
    const config = getAuthConfig();
    return apiClient.post<PasswordResetResult>(config.endpoints.requestPasswordReset, data, {
      requiresAuth: false,
    });
  },

  /**
   * Vérifier la validité d'un token de réinitialisation
   */
  async verifyResetToken(data: VerifyResetTokenData): Promise<ApiResponse<{ valid: boolean }>> {
    const config = getAuthConfig();
    return apiClient.post<{ valid: boolean }>(config.endpoints.verifyResetToken, data, {
      requiresAuth: false,
    });
  },

  /**
   * Réinitialiser le mot de passe avec un token
   */
  async resetPassword(data: ResetPasswordData): Promise<ApiResponse<void>> {
    const config = getAuthConfig();
    return apiClient.post<void>(config.endpoints.resetPassword, data, {
      requiresAuth: false,
    });
  },

  /**
   * Valider un access token
   */
  async validateToken(data: ValidateTokenData): Promise<ApiResponse<TokenValidation>> {
    const config = getAuthConfig();
    return apiClient.post<TokenValidation>(config.endpoints.validateToken, data, {
      requiresAuth: false,
    });
  },

  /**
   * Révoquer un token spécifique
   */
  async revokeToken(data: RevokeTokenData): Promise<ApiResponse<void>> {
    const config = getAuthConfig();
    return apiClient.post<void>(config.endpoints.revokeToken, data);
  },

  /**
   * Révoquer tous les tokens d'un utilisateur
   */
  async revokeAllTokens(data: RevokeAllTokensData): Promise<ApiResponse<void>> {
    const config = getAuthConfig();
    return apiClient.post<void>(config.endpoints.revokeAllTokens, data);
  },
};
